import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout, Button, Radio, Progress, Space } from 'antd';
import { 
  ArrowLeftOutlined, 
  ArrowRightOutlined, 
  CheckOutlined
} from '@ant-design/icons';
import { getRPIQuestions, getQuestionDimension } from '../data/rpiQuestions';
import './IntroPage.css';
import './QuestionnairePage.css';

const { Content } = Layout;

const QuestionnairePage = () => {
  const navigate = useNavigate();
  const { type } = useParams();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState(new Array(40).fill(null));

  const testTypeText = type === 'self' ? '给自己测' : '为恋人测';

  // 页面加载时滚动到顶部
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    
    // 检查sessionId
    const sessionId = localStorage.getItem('sessionId');
    if (!sessionId) {
      navigate('/');
      return;
    }

    // 检查是否已完成该类型的测试
    const completed = localStorage.getItem(`rpi_completed_${type}`);
    if (completed === 'true') {
      navigate(`/report/${type}`);
    }
  }, [type, navigate]);

  const questions = getRPIQuestions(type);
  const currentQ = questions[currentQuestion];
  const currentAnswer = answers[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const answeredCount = answers.filter(a => a !== null).length;
  const dimension = getQuestionDimension(currentQ.id, type);
  
  // 计算当前题在当前维度中的序号（1-10）
  const dimensionQuestionNumber = (currentQuestion % 10) + 1;

  const handleAnswer = (value) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = value;
    setAnswers(newAnswers);
    
    // 自动跳到下一题
    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      }
    }, 300);
  };

  const goToPrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const goToNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handleSubmit = () => {
    const unanswered = answers.filter(a => a === null).length;
    if (unanswered > 0) {
      alert(`还有 ${unanswered} 题未作答，请完成所有题目后提交`);
      return;
    }
    
    // 保存答案到localStorage
    const sessionId = localStorage.getItem('sessionId');
    localStorage.setItem(`rpi_answers_${type}_${sessionId}`, JSON.stringify(answers));
    
    // 跳转到分析页面（倒计时）
    navigate(`/analysis/${type}`);
  };

  return (
    <Layout className="rpi-intro-layout">
      <header className="rpi-header">
        <div className="rpi-header-content">
          <div className="rpi-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <div className="rpi-logo-icon">💕</div>
            <div className="rpi-logo-text">
              <div className="rpi-logo-title">RPI Calculator</div>
              <div className="rpi-logo-subtitle">恋爱占有欲指数计算器</div>
            </div>
          </div>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/')}
          >
            退出测评
          </Button>
        </div>
      </header>

      <Content>
        <div className="rpi-questionnaire-container">
          {/* 进度条 */}
          <div className="rpi-questionnaire-progress-section">
            <div className="rpi-questionnaire-progress-info">
              <span className="rpi-questionnaire-progress-text">
                第 {currentQuestion + 1} / {questions.length} 题
              </span>
              <span className="rpi-questionnaire-progress-answered">
                已完成 {answeredCount} 题
              </span>
            </div>
            <Progress 
              percent={Math.round(progress)} 
              strokeColor={{
                '0%': 'var(--rpi-primary)',
                '100%': 'var(--rpi-accent)'
              }}
              showInfo={false}
              strokeWidth={8}
            />
          </div>

          {/* 维度介绍（常驻显示） */}
          <div className="rpi-questionnaire-dimension-banner">
            <div className="rpi-questionnaire-dimension-header">
              <h2 className="rpi-questionnaire-dimension-title">
                {dimension.name}量表 （{type === 'self' ? '自测版' : '恋人版'}）
              </h2>
              <div className="rpi-questionnaire-dimension-count">
                第 {dimensionQuestionNumber} / 10 题
              </div>
            </div>
            <p className="rpi-questionnaire-dimension-desc">
              {dimension.description}
            </p>
          </div>

          {/* 题目卡片 */}
          <div className="rpi-questionnaire-card">
            <div className="rpi-questionnaire-question">
              <span className="rpi-questionnaire-question-number">{currentQuestion + 1}.</span>
              <span className="rpi-questionnaire-question-text">{currentQ.text}</span>
            </div>

            <Radio.Group 
              value={currentAnswer}
              onChange={(e) => handleAnswer(e.target.value)}
              className="rpi-questionnaire-options"
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                {currentQ.options.map(option => (
                  <Radio 
                    key={option.value} 
                    value={option.value}
                    className="rpi-questionnaire-option"
                  >
                    {option.label}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </div>

          {/* 导航按钮 */}
          <div className="rpi-questionnaire-nav">
            <Button
              size="large"
              onClick={goToPrevious}
              disabled={currentQuestion === 0}
              icon={<ArrowLeftOutlined />}
              className="rpi-questionnaire-btn-prev"
            >
              上一题
            </Button>
            
            <div className="rpi-questionnaire-nav-status">
              {currentAnswer !== null ? (
                <span className="rpi-questionnaire-answered-indicator">
                  <CheckOutlined /> 已回答
                </span>
              ) : (
                <span className="rpi-questionnaire-unanswered-indicator">
                  请选择答案
                </span>
              )}
            </div>

            {currentQuestion < questions.length - 1 ? (
              <Button
                size="large"
                onClick={goToNext}
                icon={<ArrowRightOutlined />}
                className="rpi-questionnaire-btn-next"
              >
                下一题
              </Button>
            ) : (
              <Button
                type="primary"
                size="large"
                onClick={handleSubmit}
                disabled={answeredCount < questions.length}
                className="rpi-questionnaire-btn-submit"
              >
                提交答案
              </Button>
            )}
          </div>

          {/* 题目跳转网格 */}
          <div className="rpi-questionnaire-jump-grid">
            {questions.map((_, index) => (
              <button
                key={index}
                className={`rpi-questionnaire-jump-btn ${index === currentQuestion ? 'active' : ''} ${answers[index] !== null ? 'answered' : ''}`}
                onClick={() => setCurrentQuestion(index)}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>
      </Content>
    </Layout>
  );
};

export default QuestionnairePage;
